import React from 'react';
import { usePublicAppContext } from '../contexts/PublicAppContext';
import { getLanguageMarket } from '../locales/index.js';
import StorageImage from './StorageImage';
import { localizeSeoHref } from '../utils/localeRouting';
import '../styles/GuestBlog.css';

const PREVIEW_DATE_OPTIONS = Object.freeze({ year: 'numeric', month: 'short', day: 'numeric' });
const BLOG_PREVIEW_COPY = {
  en: { blogBadgeGuest: 'Guest access', blogBadgeLatest: 'Latest', guestBlogTitle: 'From the Blog', guestBlogSubtitle: 'Stock keywording guides, platform rules, and metadata workflows that match buyer intent.', guestBlogPrimaryCta: 'Open Stock Photo Keyword Generator', guestBlogViewAll: 'View all blog posts' },
  tr: { blogBadgeGuest: 'Konuk erişimi', blogBadgeLatest: 'Yeni', guestBlogTitle: 'Blogdan', guestBlogSubtitle: 'Metadata iş akışınızı geliştirecek içgörüler.', guestBlogPrimaryCta: 'Stok Fotoğraf Anahtar Kelime Oluşturucuyu Aç', guestBlogViewAll: 'Tüm blog gönderilerini görüntüle' },
  es: { blogBadgeGuest: 'Acceso de invitados', blogBadgeLatest: 'El último', guestBlogTitle: 'Del blog', guestBlogSubtitle: 'Información para mejorar su flujo de trabajo de metadatos.', guestBlogPrimaryCta: 'Abrir generador de palabras clave para fotos de stock', guestBlogViewAll: 'Ver todas las publicaciones del blog' },
  fr: { blogBadgeGuest: 'Accès invité', blogBadgeLatest: 'Dernier', guestBlogTitle: 'Du blog', guestBlogSubtitle: 'Des informations pour améliorer votre flux de travail de métadonnées.', guestBlogPrimaryCta: 'Ouvrir le generateur de mots-cles photo stock', guestBlogViewAll: 'Afficher tous les articles du blog' },
  de: { blogBadgeGuest: 'Gastzugang', blogBadgeLatest: 'Letzte', guestBlogTitle: 'Aus dem Blog', guestBlogSubtitle: 'Einblicke zur Verbesserung Ihres Metadaten-Workflows.', guestBlogPrimaryCta: 'Stockfoto-Keyword-Generator offnen', guestBlogViewAll: 'Alle Blogbeiträge anzeigen' },
  it: { blogBadgeGuest: 'Accesso ospite', blogBadgeLatest: 'Ultimo', guestBlogTitle: 'Dal Blog', guestBlogSubtitle: "Guide per le parole chiave, regole della piattaforma e flussi di lavoro dei metadati che corrispondono alle intenzioni dell'acquirente.", guestBlogPrimaryCta: 'Apri Generatore di parole chiave per foto stock', guestBlogViewAll: 'Visualizza tutti i post del blog' },
  id: { blogBadgeGuest: 'Akses tamu', blogBadgeLatest: 'Terbaru', guestBlogTitle: 'Dari Blog', guestBlogSubtitle: 'Panduan kata kunci stok, aturan platform, dan alur kerja metadata yang selaras dengan intent pembeli.', guestBlogPrimaryCta: 'Buka Pembuat Kata Kunci Foto Stok', guestBlogViewAll: 'Lihat semua postingan blog' },
  th: { blogBadgeGuest: 'การเข้าถึงของแขก', blogBadgeLatest: 'ล่าสุด', guestBlogTitle: 'จากบล็อก', guestBlogSubtitle: 'จัดทำคู่มือการใช้คีย์เวิร์ด กฎของแพลตฟอร์ม และเวิร์กโฟลว์เมตาดาต้าที่ตรงกับความตั้งใจของผู้ซื้อ', guestBlogPrimaryCta: 'เปิดตัวสร้างคำสำคัญสำหรับภาพสต็อก', guestBlogViewAll: 'ดูโพสต์บล็อกทั้งหมด' }
};

const formatPreviewDate = (value, locale, options) => {
  if (!value) return '';
  try {
    const normalizedValue = String(value).trim();
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(normalizedValue);
    const date = new Date(isDateOnly ? `${normalizedValue}T12:00:00.000Z` : normalizedValue);
    return date.toLocaleDateString(
      locale,
      isDateOnly ? { ...options, timeZone: 'UTC' } : options
    );
  } catch {
    return value;
  }
};

const GuestBlogPreview = ({
  posts = [],
  onOpenBlog,
  onOpenBlogIndex,
  badgeText = '',
  isGuest = true,
  variant = 'default',
  homeContent: homeContentProp = null
}) => {
  const appContext = usePublicAppContext();
  const language = appContext?.language || 'en';
  const homeContent = homeContentProp || BLOG_PREVIEW_COPY[language] || BLOG_PREVIEW_COPY.en;
  const localeTag = getLanguageMarket(language).localeTag;
  const resolvedBadgeText = badgeText || (isGuest ? homeContent.blogBadgeGuest : homeContent.blogBadgeLatest);
  const visiblePosts = variant === 'compact' ? posts.slice(0, 3) : posts;

  if (!visiblePosts.length) return null;

  return (
    <div className={`guest-blog-section ${variant === 'compact' ? 'guest-blog-section--compact' : ''}`.trim()}>
      <div className="guest-blog-header">
        <div>
          <h2 className="guest-blog-title">{homeContent.guestBlogTitle}</h2>
          <div className="guest-blog-subtitle">{homeContent.guestBlogSubtitle}</div>
        </div>
        <div className="guest-blog-pill">{resolvedBadgeText}</div>
      </div>
      <div className={`guest-blog-grid ${variant === 'compact' ? 'guest-blog-grid--compact' : ''}`.trim()}>
        {visiblePosts.map((post) => (
          <a
            key={post.id}
            href={localizeSeoHref(`/blog/${post.slug}`, language)}
            className="guest-blog-card"
            onClick={(e) => {
              e.preventDefault();
              onOpenBlog?.(post.slug);
            }}
          >
            <div className="guest-blog-image">
              {post.image ? (
                <StorageImage
                  src={post.image}
                  alt={post.imageAlt || post.title}
                  loading="lazy"
                  decoding="async"
                  width="960"
                  height="536"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
              ) : (
                <div className="guest-blog-image-fallback" />
              )}
              <span className="guest-blog-category">{post.category}</span>
            </div>
            <div className="guest-blog-content">
              <div className="guest-blog-meta">
                <span>{post.readTime}</span>
                <span>•</span>
                <span>{formatPreviewDate(post.date, localeTag, PREVIEW_DATE_OPTIONS)}</span>
              </div>
              <h3>{post.title}</h3>
              <p>{post.excerpt}</p>
            </div>
          </a>
        ))}
      </div>
      <div className="guest-blog-footer">
        <a
          href={localizeSeoHref('/stock-photo-keyword-generator', language)}
          className="guest-blog-view-all guest-blog-view-primary"
        >
          {homeContent.guestBlogPrimaryCta}
        </a>
        <a
          href={localizeSeoHref('/blog', language)}
          className="guest-blog-view-all"
          onClick={(e) => {
            e.preventDefault();
            onOpenBlogIndex?.();
          }}
        >
          {homeContent.guestBlogViewAll}
        </a>
      </div>
    </div>
  );
};

export default GuestBlogPreview;
